import React, {useRef, useState} from 'react';
import {Alert, Box, Button, Typography} from '@mui/material';
import {readFileAsBase64} from 'helpers/readFile';
import {PdfFile} from 'hooks/useSigningDemo';
import {useTranslate} from 'hooks/useTranslate';

interface PdfUploadButtonProps {
  label: string;
  file: PdfFile;
  onChange: (file: PdfFile) => void;
}

export const PdfUploadButton = ({
  label,
  file,
  onChange,
}: PdfUploadButtonProps) => {
  const t = useTranslate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string>();

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    e.target.value = '';
    if (!selected) return;
    setError(undefined);
    if (selected.type !== 'application/pdf') {
      setError(t('demo.signing.documents.error.notPdf', {name: selected.name}));
      return;
    }
    try {
      onChange({
        fileName: selected.name,
        base64: await readFileAsBase64(selected),
      });
    } catch {
      setError(
        t('demo.signing.documents.error.readFailed', {name: selected.name}),
      );
    }
  };

  return (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
      <Typography variant="body2">
        {label}: <strong>{file.fileName}</strong>
      </Typography>
      <Button variant="outlined" onClick={() => inputRef.current?.click()}>
        {t('demo.signing.documents.uploadPdf')}
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        hidden
        data-testid="main-document-upload"
        onChange={handleFile}
      />
      {error && <Alert severity="error">{error}</Alert>}
    </Box>
  );
};
